import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import {
  Loader2, FileText, FileDown, ChevronLeft, History, Mail,
  CheckCircle2, XCircle, Trash2, Search, CalendarDays, Gavel, Users,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Textarea } from "./ui/textarea";
import { DateInput } from "./ui/date-input";

const fmt = (d) => {
  if (!d) return "—";
  const s = String(d);
  // ISO datetime με 'T' → μορφοποίηση μέσω Date
  if (s.includes("T")) {
    try {
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) {
        return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
      }
    } catch (_) { /* noop */ }
    return s;
  }
  // YYYY-MM-DD
  const parts = s.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return s;
};

const downloadBlob = async (url, fallbackName) => {
  const res = await axios.get(url, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const cd = res.headers["content-disposition"] || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

const normName = (v) => String(v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toLowerCase();

// Πληκτρολόγηση ονόματος δικαστή (ανεκτική σε πεζά/κεφαλαία/τόνους) + λίστα προτάσεων
const JudgeSearchBox = ({ judges, value, onChange, onPick }) => {
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => {
    const q = normName(value);
    if (!q) return judges;
    return judges.filter((j) => [j.judge_name, ...(j.variants || [])].some((v) => normName(v).includes(q)));
  }, [judges, value]);
  return (
    <div className="relative">
      <Label className="text-xs">Δικαστής (πληκτρολογήστε όνομα)</Label>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-3 text-slate-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter" && matches.length === 1) { onPick(matches[0].judge_name); setOpen(false); } if (e.key === "Escape") setOpen(false); }}
          placeholder="π.χ. καυκουλα, ΣΑΜΛΙΔΗΣ, Παπαδ…"
          className="w-full h-10 pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="jh-judge-input"
        />
      </div>
      {open && value.trim() && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg" data-testid="jh-judge-suggestions">
          {matches.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Κανένας δικαστής</div>}
          {matches.slice(0, 30).map((j, i) => (
            <button
              type="button"
              key={j.judge_name}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(j.judge_name); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between gap-2"
              data-testid={`jh-judge-option-${i}`}
            >
              <span>
                {j.judge_name}
                {j.duplicates && <span className="ml-2 text-[10px] text-amber-700">({j.variants.length} γραφές)</span>}
              </span>
              <span className="text-[11px] text-slate-500 whitespace-nowrap">{j.total} υποθ. · Μον. {j.by_court?.["Μονομελές"] || 0} · Πολ. {j.by_court?.["Πολυμελές"] || 0}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CourtCounts = ({ byCourt, testid }) => (
  <div className="grid grid-cols-2 gap-2 text-xs" data-testid={testid}>
    {["Μονομελές", "Πολυμελές"].map((ct) => {
      const v = (byCourt || {})[ct] || { cases: 0, with_act: 0, hearing_dates: 0 };
      return (
        <div key={ct} className={`rounded border p-2 ${ct === "Πολυμελές" ? "border-purple-200 bg-purple-50/40" : "border-blue-200 bg-blue-50/40"}`}>
          <div className="font-semibold text-slate-700">{ct}</div>
          <div><b>{v.cases}</b> υποθέσεις · <b>{v.with_act}</b> πράξεις · <b>{v.hearing_dates}</b> δικάσιμοι</div>
        </div>
      );
    })}
  </div>
);

const JudgeHistoryTab = () => {
  const { user } = useAuth();
  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];

  const [courtType, setCourtType] = useState("Όλα");
  const [judgeQuery, setJudgeQuery] = useState("");
  const [judgesList, setJudgesList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selectedJudge, setSelectedJudge] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Cancellation state
  const [cancelTarget, setCancelTarget] = useState(null); // case obj
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Hard-delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const submitCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const r = await axios.post(`${API}/pretrial-tools/cancel-assignment`, {
        case_id: cancelTarget.id,
        reason: cancelReason.trim(),
      });
      toast.success(`Ακυρώθηκε ο ορισμός: ${r.data?.previous_judge || ""} ${r.data?.previous_act ? `(Πράξη ${r.data.previous_act})` : ""}`);
      setCancelTarget(null);
      setCancelReason("");
      if (selectedJudge) openJudge(selectedJudge);
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ακύρωσης");
    } finally {
      setCancelling(false);
    }
  };

  const submitHardDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await axios.delete(`${API}/pretrial-tools/judge-assignment-history/${deleteTarget.id}`);
      toast.success(`Διαγράφηκε οριστικά: ${r.data?.previous_judge || ""}`);
      setDeleteTarget(null);
      if (selectedJudge) openJudge(selectedJudge);
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα διαγραφής");
    } finally {
      setDeleting(false);
    }
  };

  const [pinakioFilter, setPinakioFilter] = useState("");
  const [hearingFrom, setHearingFrom] = useState("");
  const [hearingTo, setHearingTo] = useState("");
  const [assignedFrom, setAssignedFrom] = useState("");
  const [assignedTo, setAssignedTo] = useState("");

  const buildParams = () => {
    const p = {};
    if (courtType && courtType !== "Όλα") p.court_type = courtType;
    if (judgeQuery.trim()) p.q = judgeQuery.trim();
    if (pinakioFilter.trim()) p.pinakio_label = pinakioFilter.trim();
    if (hearingFrom) p.hearing_date_from = hearingFrom;
    if (hearingTo) p.hearing_date_to = hearingTo;
    if (assignedFrom) p.assigned_from = assignedFrom;
    if (assignedTo) p.assigned_to = assignedTo;
    return p;
  };

  useEffect(() => { fetchSummary(); fetchJudgesList(); /* eslint-disable-next-line */ }, [courtType]);

  const fetchJudgesList = async () => {
    try {
      const params = {};
      if (courtType && courtType !== "Όλα") params.court_type = courtType;
      const r = await axios.get(`${API}/pretrial-tools/judge-assignment-history/judges`, { params });
      setJudgesList(r.data?.judges || []);
    } catch (_) { /* noop */ }
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pretrial-tools/judge-assignment-history`, { params: buildParams() });
      setSummary(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης");
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setPinakioFilter(""); setHearingFrom(""); setHearingTo(""); setJudgeQuery("");
    setAssignedFrom(""); setAssignedTo("");
    setTimeout(fetchSummary, 50);
  };

  const openJudge = async (judgeName) => {
    setSelectedJudge(judgeName);
    setDetailsLoading(true);
    try {
      const params = { ...buildParams(), judge_name: judgeName };
      const r = await axios.get(`${API}/pretrial-tools/judge-assignment-history`, { params });
      setDetails(r.data);
    } catch (e) {
      toast.error("Σφάλμα φόρτωσης ιστορικού");
    } finally {
      setDetailsLoading(false);
    }
  };

  const exportJudgeHistory = async (judgeName, fmtKind) => {
    try {
      const params = new URLSearchParams({ judge_name: judgeName, format: fmtKind });
      if (courtType && courtType !== "Όλα") params.set("court_type", courtType);
      await downloadBlob(
        `${API}/pretrial-tools/judge-assignment-history-export?${params.toString()}`,
        `history_${judgeName}.${fmtKind === "xlsx" ? "xlsx" : "pdf"}`
      );
      toast.success(`Εξαγωγή ${fmtKind.toUpperCase()} έτοιμη`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα εξαγωγής");
    }
  };

  // Shared dialogs (rendered σε ΟΛΑ τα views ώστε να ανοίγουν σωστά)
  const dialogs = (
    <>
      {/* Cancel Assignment Confirmation Dialog */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setCancelReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <XCircle className="w-5 h-5" /> Ακύρωση Ορισμού Δικαστή
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-sm">
              <span className="block">Πρόκειται να ακυρώσετε τον ορισμό για την υπόθεση:</span>
              <span className="block bg-slate-50 border rounded p-2 font-mono text-xs">
                ΓΑΚ {cancelTarget?.gak_number}/{cancelTarget?.gak_year} ·
                {cancelTarget?.assignment_act_number && <> Πράξη {cancelTarget?.assignment_act_number}/{cancelTarget?.assignment_act_year} ·</>}
                {" "}Δικαστής: <b>{cancelTarget?.assigned_judge || selectedJudge}</b>
              </span>
              <span className="block text-red-600 text-xs">
                Η ενέργεια θα διαγράψει την Πράξη Ορισμού και θα επαναφέρει την υπόθεση σε κατάσταση
                <b> προ-ορισμού</b>. Καταγράφεται στο ιστορικό ακυρώσεων για audit.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Label className="text-xs">Αιτιολογία ακύρωσης (προαιρετική):</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="π.χ. Λάθος ανάθεση, επανυπολογισμός..."
              className="mt-1 text-sm"
              rows={3}
              data-testid="cancel-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Άκυρο</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitCancel}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
              Ακύρωση Ορισμού
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-800 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> ΠΛΗΡΗΣ ΔΙΑΓΡΑΦΗ Ορισμού
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-sm">
              <span className="block">Πρόκειται να διαγράψετε <b>ΟΡΙΣΤΙΚΑ</b> τον ορισμό για:</span>
              <span className="block bg-red-50 border border-red-200 rounded p-2 font-mono text-xs">
                ΓΑΚ {deleteTarget?.gak_number}/{deleteTarget?.gak_year} ·
                Δικαστής: <b>{deleteTarget?.assigned_judge || selectedJudge}</b>
              </span>
              <span className="block text-red-700 text-xs font-semibold">
                ⚠ ΧΩΡΙΣ καταγραφή σε audit log. Δεν θα μπορείτε να ανακτήσετε αυτή την πληροφορία.
                Χρησιμοποιείται για διόρθωση λάθος εγγραφών.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Άκυρο</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitHardDelete}
              disabled={deleting}
              className="bg-red-800 hover:bg-red-900"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Οριστική Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  // Detail view για συγκεκριμένο δικαστή
  if (selectedJudge && details) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedJudge(null); setDetails(null); }} data-testid="jh-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Πίσω στη λίστα Δικαστών
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => exportJudgeHistory(selectedJudge, "pdf")} data-testid="jh-export-pdf">
              <FileDown className="w-4 h-4 mr-1" /> Εξαγωγή PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportJudgeHistory(selectedJudge, "xlsx")} data-testid="jh-export-xlsx">
              <FileText className="w-4 h-4 mr-1" /> Εξαγωγή Excel
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" /> {selectedJudge}
            </CardTitle>
            <CardDescription>
              {courtType === "Όλα" ? "Όλα τα δικαστήρια" : courtType} · Σύνολο: {details.total} υποθέσεις
              {details.summary?.duplicates && (
                <span className="block text-amber-700 text-xs mt-1" data-testid="jh-detail-variants">
                  Συγχωνεύθηκαν {details.summary.variants.length} γραφές του ονόματος: {details.summary.variants.join(" · ")}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {details.summary && (
              <div className="grid md:grid-cols-3 gap-3" data-testid="jh-detail-summary">
                <div className="rounded border p-3 bg-slate-50">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Gavel className="w-3.5 h-3.5" /> Σύνολο</div>
                  <div className="text-2xl font-bold" data-testid="jh-detail-total">{details.summary.total}</div>
                  <div className="text-xs text-slate-600"><b>{details.summary.with_act}</b> με Πράξη Ορισμού · <b>{details.summary.hearing_dates_count}</b> δικάσιμοι</div>
                </div>
                <div className="md:col-span-2"><CourtCounts byCourt={details.summary.by_court} testid="jh-detail-by-court" /></div>
                <div className="md:col-span-3">
                  <div className="text-xs text-slate-500 flex items-center gap-1 mb-1"><CalendarDays className="w-3.5 h-3.5" /> Δικάσιμοι που έχει χρεωθεί</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="jh-detail-hearings">
                    {(details.summary.hearings || []).map((h) => (
                      <Badge key={`${h.date}-${h.pinakio}`} variant="outline" className={`text-[11px] ${h.court_type === "Πολυμελές" ? "border-purple-300 text-purple-800" : "border-blue-300 text-blue-800"}`}>
                        {fmt(h.date)} · {h.pinakio || "—"} · {h.cases} υποθ.{h.with_act ? ` / ${h.with_act} πρ.` : ""}
                      </Badge>
                    ))}
                    {(details.summary.hearings || []).length === 0 && <span className="text-xs text-slate-400">—</span>}
                  </div>
                </div>
              </div>
            )}
            {detailsLoading ? (
              <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700">
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">ΓΑΚ</th>
                      <th className="p-2 text-left">ΕΑΚ</th>
                      <th className="p-2 text-left">Δικαστήριο</th>
                      <th className="p-2 text-center">Κατάθεση</th>
                      <th className="p-2 text-center">Δικάσιμος</th>
                      <th className="p-2 text-left">Πινάκιο</th>
                      <th className="p-2 text-center">Ανάθεση</th>
                      <th className="p-2 text-left">Αντικείμενο</th>
                      <th className="p-2 text-center">Πράξη</th>
                      <th className="p-2 text-center">Email</th>
                      <th className="p-2 text-center">Ενέργειες</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(details.cases || []).map((c, idx) => (
                      <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="p-2 text-slate-500">{idx + 1}</td>
                        <td className="p-2 font-mono text-xs">{c.gak_number}/{c.gak_year}</td>
                        <td className="p-2 font-mono text-xs text-blue-700">{c.eak_number || "—"}/{c.gak_year}</td>
                        <td className="p-2 text-xs">{c.court_type || "—"}</td>
                        <td className="p-2 text-center font-mono text-xs">{fmt(c.filing_date)}</td>
                        <td className="p-2 text-center font-mono text-xs">
                          {(() => {
                            // Παίρνουμε ΔΙΚΑΣΙΜΟ από hearing_date ή parse στο last_hearing_date
                            if (c.hearing_date) return fmt(c.hearing_date);
                            const m = (c.last_hearing_date || "").match(/(\d{2}\/\d{2}\/\d{4})/);
                            return m ? m[1] : <span className="text-slate-300">—</span>;
                          })()}
                        </td>
                        <td className="p-2 text-xs">
                          {(() => {
                            // Παίρνουμε το PINAKIO label (πρώτο μέρος πριν το " - ")
                            const lhd = c.last_hearing_date || "";
                            if (!lhd) return <span className="text-slate-300">—</span>;
                            const parts = lhd.split(" - ");
                            return <span className="font-mono text-blue-700" title={lhd}>{parts[0]}</span>;
                          })()}
                        </td>
                        <td className="p-2 text-center font-mono text-xs">
                          {c.assigned_judge_at ? fmt(c.assigned_judge_at.split("T")[0]) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-2 text-xs max-w-[300px] truncate">{c.subject || "—"}</td>
                        <td className="p-2 text-center">
                          {c.assignment_act_number ? (
                            <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">
                              {c.assignment_act_number}/{c.assignment_act_year}
                            </Badge>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-2 text-center">
                          {c.assignment_act_emailed_at ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => { setCancelTarget(c); setCancelReason(""); }}
                            data-testid={`jh-cancel-${c.id}`}
                            title="Ακύρωση Ορισμού & Πράξης (επαναφέρει σε προ-ορισμού)"
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1" />Ακύρωση
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 px-2 text-red-800 hover:bg-red-100 hover:text-red-900 ml-1"
                            onClick={() => setDeleteTarget(c)}
                            data-testid={`jh-delete-${c.id}`}
                            title="ΠΛΗΡΗΣ ΔΙΑΓΡΑΦΗ — διαγράφει οριστικά την ανάθεση χωρίς audit"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(details.cases || []).length === 0 && (
                  <p className="text-center py-8 text-slate-400 text-sm">Δεν βρέθηκαν υποθέσεις</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        {dialogs}
      </div>
    );
  }

  // Summary view: λίστα δικαστών με totals
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <JudgeSearchBox judges={judgesList} value={judgeQuery} onChange={setJudgeQuery} onPick={(name) => { setJudgeQuery(name); openJudge(name); }} />
            <div>
              <Label className="text-xs">Επιλογή δικαστή από λίστα ({judgesList.length})</Label>
              <Select value="" onValueChange={(name) => { setJudgeQuery(name); openJudge(name); }}>
                <SelectTrigger data-testid="jh-judge-select"><SelectValue placeholder="— Επιλέξτε δικαστή —" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {judgesList.map((j, i) => (
                    <SelectItem key={j.judge_name} value={j.judge_name} data-testid={`jh-judge-select-option-${i}`}>
                      {j.judge_name} — {j.total} υποθ. (Μον. {j.by_court?.["Μονομελές"] || 0} / Πολ. {j.by_court?.["Πολυμελές"] || 0}){j.duplicates ? ` · ${j.variants.length} γραφές` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Τύπος Δικαστηρίου</Label>
              <Select value={courtType} onValueChange={setCourtType}>
                <SelectTrigger data-testid="jh-court-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Όλα">Όλα</SelectItem>
                  {userCourtTypes.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs">Πινάκιο (πρόθεμα/τίτλος)</Label>
              <input
                type="text"
                value={pinakioFilter}
                onChange={(e) => setPinakioFilter(e.target.value)}
                placeholder="π.χ. ΚΑΛ, ΑΜΑ, ΗΑΣ, 160-ΚΑΛ"
                className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="jh-pinakio-filter"
              />
            </div>
            <div>
              <Label className="text-xs">Δικάσιμος Από</Label>
              <DateInput value={hearingFrom} onChange={(e) => setHearingFrom(e.target.value)}
                className="h-10 px-2 text-sm border border-slate-200 rounded-md w-40" data-testid="jh-hearing-from" />
            </div>
            <div>
              <Label className="text-xs">Δικάσιμος Έως</Label>
              <DateInput value={hearingTo} onChange={(e) => setHearingTo(e.target.value)}
                className="h-10 px-2 text-sm border border-slate-200 rounded-md w-40" data-testid="jh-hearing-to" />
            </div>
            <div>
              <Label className="text-xs">Ανάθεση Από</Label>
              <DateInput value={assignedFrom} onChange={(e) => setAssignedFrom(e.target.value)}
                className="h-10 px-2 text-sm border border-slate-200 rounded-md w-40" data-testid="jh-assigned-from" />
            </div>
            <div>
              <Label className="text-xs">Ανάθεση Έως</Label>
              <DateInput value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}
                className="h-10 px-2 text-sm border border-slate-200 rounded-md w-40" data-testid="jh-assigned-to" />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={fetchSummary} disabled={loading} data-testid="jh-search-btn">
              {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <History className="w-3 h-3 mr-1" />}
              Αναζήτηση
            </Button>
            <Button size="sm" variant="outline" onClick={resetFilters} data-testid="jh-reset-btn">
              Καθαρισμός Φίλτρων
            </Button>
            {summary && (
              <Badge variant="outline" className="text-xs ml-auto">
                {summary.total_judges} δικαστές · {summary.total_cases} υποθέσεις
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" /> Ιστορικό Αναθέσεων ανά Δικαστή
          </CardTitle>
          <CardDescription>
            Συνοπτική προβολή: επιλέξτε δικαστή για να δείτε αναλυτικό ιστορικό και να εξαγάγετε PDF/Excel.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-700">
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Δικαστής</th>
                    <th className="p-2 text-center">Σύνολο</th>
                    <th className="p-2 text-center">Με Πράξη</th>
                    <th className="p-2 text-center" title="Υποθέσεις / Πράξεις / Δικάσιμοι">Μονομελές<br /><span className="font-normal text-[10px]">υποθ. / πρ. / δικ.</span></th>
                    <th className="p-2 text-center" title="Υποθέσεις / Πράξεις / Δικάσιμοι">Πολυμελές<br /><span className="font-normal text-[10px]">υποθ. / πρ. / δικ.</span></th>
                    <th className="p-2 text-center">Δικάσιμοι</th>
                    <th className="p-2 text-center">Με Email</th>
                    <th className="p-2 text-center">Πρώτη Ανάθεση</th>
                    <th className="p-2 text-center">Τελευταία</th>
                    <th className="p-2 text-center">Ενέργειες</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.judges || []).map((j, idx) => (
                    <tr key={j.judge_name} className="border-b last:border-0 hover:bg-slate-50" data-testid={`jh-row-${idx}`}>
                      <td className="p-2 text-slate-500">{idx + 1}</td>
                      <td className="p-2 font-medium" data-testid={`jh-row-name-${idx}`}>
                        {j.judge_name}
                        {j.duplicates && (
                          <div className="text-[10px] text-amber-700 font-normal" title={j.variants.join(" · ")} data-testid={`jh-row-variants-${idx}`}>
                            <Users className="w-3 h-3 inline mr-0.5" />{j.variants.length} γραφές: {j.variants.filter((v) => v !== j.judge_name).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-center font-mono">
                        <Badge className="bg-blue-600 text-white" data-testid={`jh-row-total-${idx}`}>{j.total}</Badge>
                      </td>
                      <td className="p-2 text-center font-mono text-emerald-700">{j.with_act}</td>
                      <td className="p-2 text-center font-mono text-xs text-blue-800" data-testid={`jh-row-mono-${idx}`}>
                        {(j.by_court?.["Μονομελές"]?.cases || 0)} / {(j.by_court?.["Μονομελές"]?.with_act || 0)} / {(j.by_court?.["Μονομελές"]?.hearing_dates || 0)}
                      </td>
                      <td className="p-2 text-center font-mono text-xs text-purple-800" data-testid={`jh-row-poly-${idx}`}>
                        {(j.by_court?.["Πολυμελές"]?.cases || 0)} / {(j.by_court?.["Πολυμελές"]?.with_act || 0)} / {(j.by_court?.["Πολυμελές"]?.hearing_dates || 0)}
                      </td>
                      <td className="p-2 text-center font-mono text-xs" data-testid={`jh-row-hearings-${idx}`}>{j.hearing_dates_count}</td>
                      <td className="p-2 text-center font-mono text-purple-700">
                        <Mail className="w-3 h-3 inline mr-1" />{j.with_email}
                      </td>
                      <td className="p-2 text-center font-mono text-xs">{fmt(j.first_assigned_at)}</td>
                      <td className="p-2 text-center font-mono text-xs">{fmt(j.last_assigned_at)}</td>
                      <td className="p-2 text-center whitespace-nowrap">
                        <Button size="sm" variant="outline" className="h-7 px-2 mr-1" onClick={() => openJudge(j.judge_name)} data-testid={`jh-open-${idx}`}>
                          Λεπτομέρειες
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => exportJudgeHistory(j.judge_name, "pdf")} title="Εξαγωγή PDF">
                          <FileDown className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!summary?.judges || summary.judges.length === 0) && (
                <p className="text-center py-8 text-slate-400 text-sm">Δεν βρέθηκαν αναθέσεις</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      {dialogs}
    </div>
  );
};

export default JudgeHistoryTab;
